const COMPONENT_UTILS_STATE_KEY = "__BCL_COMPONENT_UTILS_STATE__";

const state = (window[COMPONENT_UTILS_STATE_KEY] = window[COMPONENT_UTILS_STATE_KEY] || {
  initializedComponentKeys: new Set(),
  pendingModulePromiseByPath: new Map(),
  pendingInitPromiseByScopeKey: new Map(),
  rootIdByNode: new WeakMap(),
  rootIdSeq: 0,
  debug: {
    cachedDebugState: null,
    didCleanupLocalStorage: false,
    storageAvailable: null,
  },
});

const { initializedComponentKeys, pendingModulePromiseByPath, pendingInitPromiseByScopeKey } = state;

const HTML_ROOT_ELEMENT = document.documentElement;

// ─── Root / Scope Helpers ────────────────────────────────────────────────────

const getRootId = (root) => {
  let id = state.rootIdByNode.get(root);
  if (!id) {
    id = `r${++state.rootIdSeq}`;
    state.rootIdByNode.set(root, id);
  }
  return id;
};

const buildComponentKey = ({ belongsTo = "", customName = "", name = "", pathBaseName = "" } = {}) => `${belongsTo || "~"}::${customName || name || pathBaseName || "UnknownComponent"}`;

const buildScopeKey = (componentKey, root) => `${componentKey}::${root === document ? "doc" : getRootId(root)}`;

// ─── Global Registry ─────────────────────────────────────────────────────────

export const mutateBarceloComponents = ({ component, name, belongsTo }) => {
  window.bcl = window.bcl || {};
  window.bcl.c = window.bcl.c || {};

  if (belongsTo) {
    window.bcl.c[belongsTo] = window.bcl.c[belongsTo] || {};
    window.bcl.c[belongsTo][name] = component;
    return;
  }

  window.bcl.c[name] = Object.assign(window.bcl.c[name] || {}, component);
};

// ─── Presence Check ──────────────────────────────────────────────────────────

export const isPresent = (selector, root = document) => {
  if (!selector || !root) {
    return false;
  }
  if (Array.isArray(selector)) {
    return selector.some((s) => s && root.querySelector(s));
  }
  return !!root.querySelector(selector);
};

// ─── Debug System ────────────────────────────────────────────────────────────

window.bcl = window.bcl || {};
window.bcl.debug = window.bcl.debug || {};

const DEBUG_TTL_MS = 60 * 60 * 1000;
const LS_KEY_DEBUG_FLAG = "BCL_DEBUG_COMPONENTS";
const LS_KEY_DEBUG_UNTIL = "BCL_DEBUG_COMPONENTS_UNTIL";

const getLocalStorage = () => {
  const debugState = state.debug;
  if (debugState.storageAvailable === false) {
    return null;
  }

  try {
    const storage = window.localStorage;
    if (!storage) {
      debugState.storageAvailable = false;
      return null;
    }

    storage.setItem("__bcl_ls_test__", "1");
    storage.removeItem("__bcl_ls_test__");
    debugState.storageAvailable = true;
    return storage;
  } catch {
    debugState.storageAvailable = false;
    return null;
  }
};

const cleanupExpiredDebugEntry = () => {
  const debugState = state.debug;
  if (debugState.didCleanupLocalStorage) {
    return;
  }
  debugState.didCleanupLocalStorage = true;

  const storage = getLocalStorage();
  if (!storage) {
    return;
  }

  try {
    const expiresAt = parseInt(storage.getItem(LS_KEY_DEBUG_UNTIL) || "0", 10);
    if (expiresAt > 0 && expiresAt <= Date.now()) {
      storage.removeItem(LS_KEY_DEBUG_FLAG);
      storage.removeItem(LS_KEY_DEBUG_UNTIL);
    }
  } catch (err) {
    console.error("[BarceloComponent] Failed to cleanup expired debug state:", err);
  }
};

const computeDebugOn = () => {
  const debugState = state.debug;
  if (debugState.cachedDebugState !== null) {
    return debugState.cachedDebugState;
  }

  const cacheAndReturn = (value) => {
    debugState.cachedDebugState = value;
    return value;
  };

  try {
    cleanupExpiredDebugEntry();

    if (typeof window.bcl.debug.componentsFlag !== "undefined") {
      return cacheAndReturn(!!window.bcl.debug.componentsFlag);
    }

    const storage = getLocalStorage();
    if (!storage) {
      return cacheAndReturn(false);
    }

    const storedFlag = storage.getItem(LS_KEY_DEBUG_FLAG);
    const expiresAt = parseInt(storage.getItem(LS_KEY_DEBUG_UNTIL) || "0", 10);

    if (storedFlag != null && expiresAt > Date.now()) {
      return cacheAndReturn(storedFlag === "1" || storedFlag === "true");
    }
  } catch (err) {
    console.error("[BarceloComponent] Failed to compute debug state:", err);
  }

  return cacheAndReturn(false);
};

export const setComponentsDebug = (on, { ttlMs = DEBUG_TTL_MS } = {}) => {
  const enabled = !!on;
  window.bcl.debug.componentsFlag = enabled;
  state.debug.cachedDebugState = enabled;

  try {
    const storage = getLocalStorage();

    if (enabled) {
      const ttl = Number.isFinite(ttlMs) && ttlMs > 0 ? ttlMs : DEBUG_TTL_MS;
      const expiresAt = Date.now() + ttl;
      const minutes = Math.round(ttl / 60000);

      storage?.setItem(LS_KEY_DEBUG_FLAG, "1");
      storage?.setItem(LS_KEY_DEBUG_UNTIL, String(expiresAt));

      console.info(`[BarceloComponent] Debug enabled for ${minutes} minute${minutes !== 1 ? "s" : ""}.`, `Auto-disables at ${new Date(expiresAt).toLocaleTimeString()}.`, `To disable early: bcl.debug.components = false`);
      return;
    }

    storage?.removeItem(LS_KEY_DEBUG_FLAG);
    storage?.removeItem(LS_KEY_DEBUG_UNTIL);
    console.info("[BarceloComponent] Component debug disabled.");
  } catch (err) {
    console.error("[BarceloComponent] Failed to set debug state:", err);
  }
};

if (!Object.prototype.hasOwnProperty.call(window.bcl.debug, "components")) {
  try {
    Object.defineProperty(window.bcl.debug, "components", {
      configurable: true,
      get() {
        return computeDebugOn();
      },
      set(value) {
        setComponentsDebug(!!value);
      },
    });
  } catch {
    window.bcl.debug.components = false;
  }
}

const debugWarn = (...args) => {
  if (computeDebugOn()) {
    console.warn("[BarceloComponent]", ...args);
  }
};
const debugError = (...args) => {
  if (computeDebugOn()) {
    console.error("[BarceloComponent]", ...args);
  }
};

// ─── Dynamic Import ──────────────────────────────────────────────────────────

export const dynamicImport = async ({ customName, name, belongsTo, component, initMethod, skipInit, root = document, pathBaseName = "" }) => {
  const componentName = customName || name || pathBaseName || "UnknownComponent";
  const componentExport = component?.default;

  if (!componentExport) {
    return null;
  }

  mutateBarceloComponents({ component: componentExport, name: componentName, belongsTo });

  if (skipInit || typeof componentExport?.[initMethod] !== "function") {
    return componentExport;
  }

  const globalInitKey = `${belongsTo || ""}:${componentName}`;
  if (root === document && initializedComponentKeys.has(globalInitKey)) {
    return componentExport;
  }

  const componentKey = buildComponentKey({ belongsTo, customName, name, pathBaseName });
  const initScopeKey = `${buildScopeKey(componentKey, root)}::init`;

  const pendingInit = pendingInitPromiseByScopeKey.get(initScopeKey);
  if (pendingInit) {
    await pendingInit;
    return componentExport;
  }

  const initTask = Promise.resolve(componentExport[initMethod](root))
    .then(() => {
      if (root === document) {
        initializedComponentKeys.add(globalInitKey);
      }
    })
    .finally(() => pendingInitPromiseByScopeKey.delete(initScopeKey));

  pendingInitPromiseByScopeKey.set(initScopeKey, initTask);
  await initTask;
  return componentExport;
};

// ─── Component Factory ───────────────────────────────────────────────────────

export const BarceloComponent = ({
  selector,
  path,
  initMethod = "init",
  skipInit = false,
  belongsTo = "",
  customName = "",
  forceImport = false,
  skipForceIfPresent = [],
  enableObserver = false,
  ignoreViewport = false,
  mobileDelay = 0,
  mobileInteractionSelector = "",
} = {}) => {
  const pathBaseName = path ? path.split("/").pop().replace(".js", "") : "";
  const importKey = path ? `./${path}.js` : "";

  const rawSkipConditions = Array.isArray(skipForceIfPresent) ? skipForceIfPresent : skipForceIfPresent ? [skipForceIfPresent] : [];

  const parsedSkipConditions = rawSkipConditions.reduce((result, conditionRaw) => {
    if (!conditionRaw || typeof conditionRaw !== "string") {
      return result;
    }
    let condition = conditionRaw.trim();
    if (!condition) {
      return result;
    }

    const isNegated = condition[0] === "!";
    if (isNegated) {
      condition = condition.slice(1).trim();
    }
    if (!condition) {
      return result;
    }

    if (condition.startsWith("data-")) {
      const eqIndex = condition.indexOf("=");
      result.push(eqIndex === -1 ? { type: "attr", isNegated, attr: condition, value: null } : { type: "attr", isNegated, attr: condition.slice(0, eqIndex), value: condition.slice(eqIndex + 1) });
    } else {
      result.push({ type: "selector", isNegated, selector: condition });
    }

    return result;
  }, []);

  const resolveName = (name) => customName || name || pathBaseName || "UnknownComponent";
  const formatName = (name) => {
    const base = resolveName(name);
    return belongsTo ? `${base} [${belongsTo}]` : base;
  };

  const shouldSkipForceImport = (root = document) => {
    for (const condition of parsedSkipConditions) {
      let matches = false;

      if (condition.type === "attr") {
        matches = condition.value == null ? HTML_ROOT_ELEMENT.hasAttribute(condition.attr) : HTML_ROOT_ELEMENT.hasAttribute(condition.attr) && HTML_ROOT_ELEMENT.getAttribute(condition.attr) === condition.value;
      } else if (typeof root.querySelector === "function") {
        matches = !!root.querySelector(condition.selector);
      }

      if (condition.isNegated ? !matches : matches) {
        return true;
      }
    }
    return false;
  };

  return {
    forceImport,
    enableObserver,
    ignoreViewport,
    mobileDelay,
    mobileInteractionSelector,

    isPresent({ root = document, ignoreForceImport = false, ignoreIsPresent = false } = {}) {
      const skipForce = shouldSkipForceImport(root);
      return ignoreIsPresent || (!ignoreForceImport && forceImport && !skipForce) || isPresent(selector, root);
    },

    async importComponent({ name, root = document }) {
      if (forceImport && shouldSkipForceImport(root)) {
        return null;
      }

      if (!importKey) {
        debugWarn(`❌ ${formatName(name)} - FAIL — missing path`);
        return null;
      }

      let modulePromise = pendingModulePromiseByPath.get(importKey);
      if (!modulePromise) {
        modulePromise = import(`./${path}.js`).finally(() => pendingModulePromiseByPath.delete(importKey));
        pendingModulePromiseByPath.set(importKey, modulePromise);
      }

      try {
        const importedModule = await modulePromise;

        if (!importedModule?.default) {
          debugWarn(`❌ ${formatName(name)} - FAIL — no default export`);
          return null;
        }

        return await dynamicImport({
          customName,
          name,
          component: importedModule,
          skipInit,
          belongsTo,
          initMethod,
          root,
          pathBaseName,
        });
      } catch (err) {
        const errorMessage = err?.message || err?.toString() || "";
        debugError(`❌ ${formatName(name)} - FAIL${errorMessage ? ` — ${errorMessage}` : ""}`, err);
        throw err;
      }
    },

    componentClass() {
      return { selector };
    },
  };
};
